import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { useT } from '@/lib/i18n';
import { UserPlus, Trash2, User, Mail, Phone, Crown, LogIn, Pencil, Star, Check, X } from 'lucide-react';

export default function Contacts() {
  const user = useApp((s) => s.user);
  const familyMode = useApp((s) => s.familyMode);
  const t = useT();
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // inline edit state
  const [editingId, setEditingId] = useState(null);
  const [eName, setEName] = useState('');
  const [eEmail, setEEmail] = useState('');
  const [ePhone, setEPhone] = useState('');

  useEffect(() => {
    if (user) api.listContacts().then(setContacts).catch(() => {});
    else {
      const local = localStorage.getItem('sakaysafe-guest-contact');
      if (local) { try { const c = JSON.parse(local); if (c.name) setContacts([{ id: 'local', ...c, is_primary: true }]); } catch (e) { console.debug('Could not parse local guest contact:', e); } }
    }
  }, [user]);

  const add = async () => {
    if (!name) { toast.error(t('c_need_name')); return; }
    if (user) {
      try { const c = await api.createContact({ name, email, phone, is_primary: contacts.length === 0 }); setContacts((p) => [...p, c]); setName(''); setEmail(''); setPhone(''); toast.success(t('c_saved')); }
      catch { toast.error(t('c_save_err')); }
    } else {
      const c = { name, email, phone };
      localStorage.setItem('sakaysafe-guest-contact', JSON.stringify(c));
      setContacts([{ id: 'local', ...c, is_primary: true }]);
      setName(''); setEmail(''); setPhone(''); toast.success(t('c_saved_local'));
    }
  };

  const del = async (c) => {
    if (user && c.id !== 'local') { try { await api.deleteContact(c.id); } catch (err) { console.warn('Delete contact failed:', err); } }
    else localStorage.removeItem('sakaysafe-guest-contact');
    setContacts((p) => p.filter((x) => x.id !== c.id));
    if (editingId === c.id) setEditingId(null);
  };

  const startEdit = (c) => { setEditingId(c.id); setEName(c.name || ''); setEEmail(c.email || ''); setEPhone(c.phone || ''); };
  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (c) => {
    if (!eName.trim()) { toast.error(t('c_need_name')); return; }
    if (user && c.id !== 'local') {
      try {
        const upd = await api.updateContact(c.id, { name: eName.trim(), email: eEmail.trim(), phone: ePhone.trim() });
        setContacts((p) => p.map((x) => (x.id === c.id ? upd : x)));
        toast.success(t('c_updated'));
      } catch { toast.error(t('c_save_err')); return; }
    } else {
      const nc = { name: eName.trim(), email: eEmail.trim(), phone: ePhone.trim() };
      localStorage.setItem('sakaysafe-guest-contact', JSON.stringify(nc));
      setContacts([{ id: 'local', ...nc, is_primary: true }]);
      toast.success(t('c_updated'));
    }
    setEditingId(null);
  };

  const setDefault = async (c) => {
    try {
      await api.updateContact(c.id, { is_primary: true });
      setContacts((p) => p.map((x) => ({ ...x, is_primary: x.id === c.id })));
      toast.success(t('c_default_set'));
    } catch { toast.error(t('c_save_err')); }
  };

  const isFamily = user?.subscription_tier === 'family';
  const atFreeLimit = !isFamily && contacts.length >= 1;

  const inputCls = 'flex items-center gap-2 rounded-xl px-3 border min-h-[48px]';

  return (
    <AppShell>
      <PageHeader title={t('c_title')} subtitle={t('c_sub')} />

      <div className="space-y-2 mb-4">
        {contacts.length === 0 && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('c_addone')}</p>}
        {contacts.map((c) => (
          <div key={c.id} className="rounded-2xl p-3 border" style={{ background: 'var(--surface)', borderColor: editingId === c.id ? 'var(--primary)' : 'var(--border)' }} data-testid="contact-item">
            {editingId === c.id ? (
              <div className="space-y-2" data-testid="contact-edit-form">
                <div className={inputCls} style={{ borderColor: 'var(--border)' }}><User size={18} style={{ color: 'var(--text-muted)' }} /><input value={eName} onChange={(e) => setEName(e.target.value)} placeholder={t('c_name')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="contact-edit-name" /></div>
                <div className={inputCls} style={{ borderColor: 'var(--border)' }}><Mail size={18} style={{ color: 'var(--text-muted)' }} /><input value={eEmail} onChange={(e) => setEEmail(e.target.value)} placeholder={t('c_email')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="contact-edit-email" /></div>
                <div className={inputCls} style={{ borderColor: 'var(--border)' }}><Phone size={18} style={{ color: 'var(--text-muted)' }} /><input value={ePhone} onChange={(e) => setEPhone(e.target.value)} placeholder={t('c_phone')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="contact-edit-phone" /></div>
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(c)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full font-bold min-h-[44px] text-sm" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="contact-edit-save"><Check size={16} /> {t('c_save')}</button>
                  <button onClick={cancelEdit} className="inline-flex items-center justify-center gap-1.5 rounded-full font-semibold min-h-[44px] px-4 text-sm border" style={{ borderColor: 'var(--border)', color: 'var(--text)' }} data-testid="contact-edit-cancel"><X size={16} /> {t('c_cancel')}</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }}><User size={20} /></span>
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-1 truncate" style={{ color: 'var(--text)' }}>{c.name} {c.is_primary && <span className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'var(--surface-alt)', color: 'var(--primary)' }}>{t('c_primary')}</span>}</div>
                    <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{c.email || c.phone || t('c_noinfo')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {user && c.id !== 'local' && !c.is_primary && (
                    <button onClick={() => setDefault(c)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ color: 'var(--text-muted)' }} aria-label={t('c_setdefault')} title={t('c_setdefault')} data-testid="set-default-contact-button"><Star size={17} /></button>
                  )}
                  <button onClick={() => startEdit(c)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ color: 'var(--text-muted)' }} aria-label={t('c_edit')} title={t('c_edit')} data-testid="edit-contact-button"><Pencil size={17} /></button>
                  <button onClick={() => del(c)} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ color: 'var(--danger)' }} aria-label={t('c_delete')} title={t('c_delete')} data-testid="delete-contact-button"><Trash2 size={18} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {atFreeLimit ? (
        <div className="rounded-2xl p-4 border" style={{ background: 'var(--surface-alt)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2"><Crown size={18} style={{ color: 'var(--primary)' }} /><span className="font-bold" style={{ color: 'var(--text)' }}>{familyMode ? t('c_family_t') : t('c_free_limit_t')}</span></div>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{familyMode ? t('c_family_b') : t('c_free_limit_b')}</p>
        </div>
      ) : (
        <div className="rounded-2xl p-4 border space-y-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="add-contact-form">
          <div className={inputCls} style={{ borderColor: 'var(--border)' }}><User size={18} style={{ color: 'var(--text-muted)' }} /><input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('c_name')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="contact-name-input" /></div>
          <div className={inputCls} style={{ borderColor: 'var(--border)' }}><Mail size={18} style={{ color: 'var(--text-muted)' }} /><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('c_email')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="contact-email-input" /></div>
          <div className={inputCls} style={{ borderColor: 'var(--border)' }}><Phone size={18} style={{ color: 'var(--text-muted)' }} /><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('c_phone')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} /></div>
          <button onClick={add} className="w-full inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[48px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="add-contact-button"><UserPlus size={18} /> {t('c_save')}</button>
        </div>
      )}

      {!user && (
        <div className="rounded-2xl p-4 border mt-4 flex items-start gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <LogIn size={18} style={{ color: 'var(--primary)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('c_signin_note')}</p>
        </div>
      )}
    </AppShell>
  );
}
